import { Injectable } from '@angular/core'
import { HttpParams } from '@angular/common/http'
import { combineLatest, Observable, of } from 'rxjs'
import { shareReplay, switchMap } from 'rxjs/operators'
import { ScheduleLayout, ScheduleSearchPartial } from '@flocasts/experience-service-types'
import { EXPERIENCE_API_SCHEDULE_BASE_PATH, EXPERIENCE_API_VERSION } from '../app.config'
import { CacheDataService } from '../singleton-services/cache-data.service'
import { VerticalService } from '../singleton-services/vertical.service'
import { SiteIds } from '../shared/models/site.model'
import { DataService } from '../data/data.service'
import {
  EXP_NEXT_GEN_EVENT_HUB_WRESTLING,
  EXP_NEXT_GEN_EVENT_HUB_WRESTLING_SHORT,
  EXP_NEXT_GEN_EVENT_HUB_WRESTLING_SHORT_VARIATION,
  EXP_NEXT_GEN_EVENT_HUB_WRESTLING_VARIATION
} from '../shared/experimentation/experiment.model'
import { ExperimentationService } from '../shared/experimentation/experimentation.service'

@Injectable()
export class ScheduleDataService {
  constructor(
    private readonly cacheDataService: CacheDataService,
    private readonly verticalService: VerticalService,
    private readonly dataService: DataService,
    private readonly experimentationService: ExperimentationService
  ) {}

  public readonly isNextGenEventHub$ = this.experimentationService.isInVariation(
    EXP_NEXT_GEN_EVENT_HUB_WRESTLING,
    EXP_NEXT_GEN_EVENT_HUB_WRESTLING_VARIATION,
    true
  )
  public readonly isNextGenEventHubShort$ = this.experimentationService.isInVariation(
    EXP_NEXT_GEN_EVENT_HUB_WRESTLING_SHORT,
    EXP_NEXT_GEN_EVENT_HUB_WRESTLING_SHORT_VARIATION,
    true
  )
  public isNextGen$ = combineLatest([this.isNextGenEventHubShort$, this.isNextGenEventHub$]).pipe(
    switchMap(([nextGenShort, nextGen]) => {
      return of(nextGen || nextGenShort)
    }),
    shareReplay(1)
  )

  public readonly siteId$ = this.verticalService.siteId$
  public getScheduleExperience(
    date?: string,
    search?: string,
    facets?: string,
    isInGeoBlockingExperimentVariation?: boolean,
    isMultidayScheduleEnabled?: boolean
  ): Observable<ScheduleLayout | undefined> {
    return combineLatest([this.isNextGen$, this.siteId$]).pipe(
      switchMap(([isNextGen, siteId]) => {
        let params = new HttpParams({
          fromObject: {
            version: EXPERIENCE_API_VERSION,
            site_id: siteId || SiteIds.FLODOGS,
            limit: '100',
            offset: '0',
            tz: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles',
            showFavoriteIcon: true,
            isNextGenEventHub: isNextGen
          }
        })
        params = isInGeoBlockingExperimentVariation
          ? params.set('enableGeoBlock', isInGeoBlockingExperimentVariation)
          : params
        params = isMultidayScheduleEnabled ? params.set('enableMultiday', isMultidayScheduleEnabled) : params
        params = search ? params.set('search', search) : params
        params = facets ? params.set('facets', facets) : params

        const path = date ? `${EXPERIENCE_API_SCHEDULE_BASE_PATH}/${date}` : EXPERIENCE_API_SCHEDULE_BASE_PATH

        return this.cacheDataService.wrapper<ScheduleLayout>(path, params, true)
      })
    )
  }
  public getSchedulePaginatedPartial(endpoint: string): Observable<ScheduleSearchPartial | undefined> {
    return this.dataService.get<ScheduleSearchPartial>(endpoint)
  }
}
